import { Injectable, Injector } from '@angular/core';
import { Dictionary } from '@type_definitions/vendor';
import {
  BrowseListConfigService,
  GaVersion,
  IBrowseListComponentAttributes,
  IBrowseListComponentConfig,
  IBrowseListConfig,
  IGA2BrowselistInputBindings,
  IGA2BrowselistOutputBindings,
  ReloadRows
} from './browselists-config.service';
import { FormattingService } from '../services/formatting.service';
import { HttpDataService } from '../http/http-data.service';
import { ReportGridService } from '../report/report-grid.service';
import { IReportData, IReportResponse, IReportRow, ReportDataService } from '../report/report-data.service';
import { AuthService } from '../services/auth/auth.service';
import { ReportFiltersUtil } from '../report/report-filters-util.service';
import {
  CallContextType,
  EVENT_DELETE_ACTION,
  FormsService,
  GA3FormVisibility,
  IGA3CallContext,
  IGABrowselistFormTypeValue,
  IGAFormAction,
  IGAFormActionEventRequest,
  IGAFormMetadata,
  IGAFormTypeValue,
  TYPE_DELETE,
  ValueOrigin
} from '@app/main/forms/forms.service';
import { FormsDataService } from '@app/main/forms/forms-data.service';
import { GA1Service, MESSAGE_BROWSELIST_EDIT_DIALOG_CLOSED } from '../ga1/ga1.service';
import { util } from '../util';
import { EVENT_CLOSE_DIALOG, GaEventService, IGAEvent } from '../services/ga-event.service';
import { IObject } from '@type_definitions/datatypes';
import { DialogSize, GaMessageService, IDialog, IDialogOptions, MessageType } from '../message/ga-message.service';
import { TranslationService } from '../translation/translation.service';
import { GaDeferredService, IGADeferred } from '../services/ga-deferred.service';
import { Subscription } from 'rxjs';
import * as _ from 'underscore';
import { FormsComponent } from '@app/main/forms/forms.component';
import { BrowselistsPrecondition } from './browselists-precondition';
import { ActionStatus, REPORT_CELL_PROPERTIES } from '../report/report-style.service';
import { GaStateService, TranslationCategory } from '../services/ga-state.service';

@Injectable()
export class BrowseListService {
  private static BUTTON_OK:string = 'OK';
  private static BUTTON_CANCEL:string = 'CANCEL';
  private eventSubscription:Subscription;

  public constructor(
    private browseListConfigService: BrowseListConfigService,
    private formattingService: FormattingService,
    private httpDataService: HttpDataService,
    private messageService: GaMessageService,
    private reportGridService: ReportGridService,
    private reportDataService: ReportDataService,
    private authService: AuthService,
    private reportFiltersUtil: ReportFiltersUtil,
    private translationService: TranslationService,
    private formsService: FormsService,
    private formsDataService: FormsDataService,
    private ga1Service: GA1Service,
    private deferredService: GaDeferredService,
    private gaEventService: GaEventService,
    private stateService:GaStateService,
    private injector: Injector
  ) {
    /**/
  }

  private verifySelection(gridId:string, label: string, rowIndex:number):number[] {
    if (util.notNullOrUndefined(rowIndex)) {
      return [ rowIndex ];
    }

    const selectedIndices:number[] = this.reportGridService.getSelectedRowIndices(gridId);
    if (selectedIndices.length) {
      return selectedIndices;
    } else {
      this.messageService.showErrorDialog(`Select ${label}`, `Please select at least one ${label} first`);
    }
  }

  // deleteRow can delete one or multiple rows: 1) one row when rowIndex is specified; 2) multiple rows when ag-grid provides a list of ids for the rows selected via checkboxes (rowIndex being not specified)
  // title is only used when the browselist config doesn't have a label
  public deleteRow(gridId:string, browseListConfig:IBrowseListConfig, queryFilters: IObject, loadRowFn: () => Promise<any>, rowIndex?:number, browselistContext?: IObject, title?: string): Promise<any> {
    const label = browseListConfig.label || title;
    const selectedRowIds:number[] = this.verifySelection(gridId, label, rowIndex);
    if (!selectedRowIds) {
      return Promise.reject();
    }

    //clear the action status
    //delete from server
    //remove from table or mark the error
    return this.showDeleteConfirmDialog(selectedRowIds, browseListConfig, label)
      .then(() => {
        const deletingMessage:Noty = this.messageService.showStatusMessage(this.translationService.instant('GA.GENERAL.DELETING', { label }));
        this.reportGridService.clearActionStatus(gridId);

        const deletedRowIds:number[] = []; // a list of row ids that were confirmed by the server
        const performDelete:Function = (rowId:number):Promise<any> => {
          const entityRow:IReportRow = this.reportGridService.getRowData(browseListConfig.reportId, rowId);
          return this.deleteEntityFromServer(browseListConfig, entityRow, gridId, browselistContext, label)
            .then(() => {
              deletedRowIds.push(rowId);
            });
        };

        // delete browselist rows in a sequential order by waiting for the first promise to be resolved before calling the second promise (like an 'each' executed in an asynchronous way)
        return this.sequentialPromise(performDelete, selectedRowIds)
          .then(() => deletedRowIds)
          .finally(() => {
            this.messageService.closeMessage(deletingMessage);
          });
      })
      .then((deletedRowIds: number[]) => this.afterDelete(gridId, deletedRowIds, browseListConfig, queryFilters, loadRowFn))
      .catch(() => Promise.resolve());
  }

  private afterDelete(gridId: string, deletedRowIds: number[], config: IBrowseListConfig, queryFilters: IObject, loadRowFn: () => Promise<any>): Promise<any> | void {
    if (config.reloadRows === ReloadRows.ALL && !_.isEmpty(queryFilters)) {
      // reload the full table results
      return loadRowFn();
    } else {
      // remove selected rows from ag-grid
      this.reportGridService.removeRowData(gridId, deletedRowIds);
      return Promise.resolve();
    }
  }

  private sequentialPromise = (performDelete:Function, selectedRowIds:number[]): Promise<any> => {
    // this allows to stub sequentialPromise in unit tests
    return util.sequentialPromise(performDelete, selectedRowIds);
  };

  private showDeleteConfirmDialog(selectedRowIds:number[], config:IBrowseListConfig, label:string): Promise<void | string> {
    const deferred: IGADeferred<void | string> = this.deferredService.defer();

    const getMessage = (): string => {
      let message:string = '<h4>Are you sure you want to delete the following?</h4>';

      message += '<ul>';
      _.each(selectedRowIds, (selectedRowId:number) => {
        const entityRow:IReportRow = this.reportGridService.getRowData(config.reportId, selectedRowId);
        const entityId:number | string = this.getEntityId(entityRow, config);
        message += `<li>${label} ${entityId}</li>`;
      });
      message += '</ul>';

      return message;
    };

    this.messageService.showDialog({
      title: `Delete ${label}`,
      message: getMessage(),
      size: DialogSize.MD,
      buttons: [
        {
          label: `Delete ${label}`,
          className: 'btn-danger',
          id: BrowseListService.BUTTON_OK
        },
        {
          label: 'Don\'t Delete',
          className: 'btn-success',
          id: BrowseListService.BUTTON_CANCEL
        }
      ],
      callback: (buttonPressed:string) => {
        return buttonPressed === BrowseListService.BUTTON_OK? deferred.resolve() : deferred.reject(buttonPressed);
      }
    });

    return deferred.promise;
  }

  private getEntityId(entityRow:IReportRow, config:IBrowseListConfig): number | string {
    if (config.id) {
      return config.id;
    }
    const cell = entityRow && entityRow[ config.dataBindings.idKey ];
    return util.extractValue(cell);
  }

  /*
  ** @description transform model value comes from $scope.bindings.model to dataBindings properties value
  ** @param config {IBrowselistConfig} - active browselist config
  ** @param model {any} - model of formly form in dialog
  ** @return object
   */
  private transformModelIntoDataBindingsValue(config:IBrowseListConfig, model:any): IObject {
    const keys: IObject = {};

    if (model) {
      _.forEach(config.dataBindings, (value:string | number, prop:string) => {
        const modelProps:string = prop === 'idKey'? 'id' : prop;
        keys[ modelProps ] = util.extractValue(model[ modelProps ]);
      });
    }

    return keys;
  }

  private prepareGA1Url(url:string, queryFilters:any, config?:IBrowseListConfig, entityRow?:IReportRow): string {
    let replacedUrl:string = url;
    // transform ${} string in ga1 url to real value
    const extraKeys: IObject = this.reportGridService.transformDataBindingsToValue(config?.dataBindings, entityRow);
    const entity: IObject = util.copyAndExtend(
      queryFilters || {}, // merge with query filters
      { id: this.getEntityId(entityRow, config) }, // merge with entity id
      extraKeys, // merge with entityRow defined in selected row (if any)
      config || {} // merge with active browselist config, this is also possible coming from screenType POPUP cdata properties in report xml
    );

    replacedUrl = this.formattingService.substituteText(replacedUrl, entity);
    replacedUrl = replacedUrl.replace(/\${(\w+)}/g, ''); //remove all that have no value

    return this.authService.authenticatedUrl(replacedUrl);
  }

  private getBrowselistComponent(config:IBrowseListConfig, key:string): IBrowseListComponentConfig | IBrowseListComponentConfig[] {
    let componentConfig:string | IBrowseListComponentConfig | IBrowseListComponentConfig[] = _.clone(config[key]);

    if (_.isString(componentConfig)) {
      const componentAndIndex:string[] = (<string>componentConfig).replace(/=/g, '').split(/[\[\]]/);
      componentConfig = _.clone(config[componentAndIndex[0]]);
      if (componentAndIndex.length > 1) {
        componentConfig = (<IBrowseListComponentConfig[]>componentConfig)[ componentAndIndex[ 1 ] ];
      }
    }

    return <IBrowseListComponentConfig | IBrowseListComponentConfig[]>componentConfig;
  }

  private showAndSelectRow(gridId:string, rowIndex:number): void {
    this.reportGridService.ensureIndexVisible(gridId, rowIndex);
    this.reportGridService.clickRow(gridId, rowIndex);
  }

  public addRow(gridId:string, config:IBrowseListConfig, queryFilters:IObject, browselistContext?:any, loadAllRowsFn?:() => Promise<string | IReportData>, title?:string):void {
    this.add(gridId, config, queryFilters, browselistContext, title)
      .then((savedRow:IReportRow) => this.afterAdd(gridId, config, queryFilters, loadAllRowsFn, savedRow))
      .catch(util.noop);
  }

  private add(gridId:string, config:IBrowseListConfig, queryFilters:IObject, browselistContext?:any, title?:string):Promise<any | IReportRow> {
    // when adding, we don't support multiple configs
    const componentConfig:IBrowseListComponentConfig = <IBrowseListComponentConfig>this.getBrowselistComponent(config, 'addComponent');
    if (this.checkPreconditionFunction(config, queryFilters)) {
      return Promise.reject();
    }

    //clear the action status
    this.reportGridService.clearActionStatus(gridId);

    const label = config.label || title;

    const loadRow = (id?:string | number, entityRow?:IReportRow): Promise<IReportRow> => {
      return this.loadRow(gridId, queryFilters, id, config, entityRow);
    };

    if (componentConfig.use === GaVersion.GA1) {
      const url: string = componentConfig.url;
      const addUrl: string = this.prepareGA1Url(url, queryFilters, config);

      return this.showGA1AddEditComponentDialog('New', addUrl, config, loadRow, componentConfig, undefined);
    } else if (componentConfig.use === GaVersion.GA2) {
      const saveFunction: Function = (model:any):Promise<any> => {
        //send to server when ok is pressed
        //return result from server
        //reject when cancel is pressed or something is wrong
        const msg: Noty = this.messageService.showStatusMessage(`Creating ${label} ...`);
        return this.addEntityToServer(config, model)
          .then((savedEntity) => {
            // For ReloadRows -> ALL / NONE
            if (config.reloadRows !== ReloadRows.SINGLE) {
              // save entity id when reloadRows is enabled and do not load a single row as a full ag-grid reload will be performed
              // or do nothing when reload rows "NONE" set
              return Promise.resolve(savedEntity?.id);
            } else {
              return loadRow(savedEntity.id);
            }
          })
          .finally(() => {
            this.messageService.closeMessage(msg);
          });
      };

      return this.showGA2AddEditComponentDialog(browselistContext, config, componentConfig, undefined, loadRow, saveFunction, queryFilters, label);
    } else {
      return this.showGA3FormInDialog(undefined, config, componentConfig, loadRow, queryFilters, undefined, browselistContext, label);
    }
  }

  /**
   * @private afterAdd
   * @param gridId {string} - Grid ID
   * @param browseListConfig {IBrowseListConfig} - Current selected browselist config
   * @param queryFilters {IObject} - Applied pre-defined query filters
   * @param loadAllRowsFn {() => Promise<string>} - Load all rows function
   * @param savedRow {IReportRow | number} - if type is number then it already contains row primary ID value, otherwise it contains an object of one row
   * @return void
   */
  private afterAdd(gridId:string, browseListConfig:IBrowseListConfig, queryFilters:IObject, loadAllRowsFn:() => Promise<string | IReportData>, savedRow:IReportRow|number):void {
    const idKey:string = browseListConfig?.dataBindings?.idKey;
    const getRowIndex:Function = (valueId:number):number => {
      return this.reportGridService.getIndex(gridId, idKey, valueId);
    };

    if (browseListConfig.reloadRows === ReloadRows.ALL && !_.isEmpty(queryFilters)) {
      // Reload the full table results
      // When 'loadAllRowsFn' is resolved, we expect the new data to have been inserted into the grid by passed method in arguments.
      // If not, we will attempt to re-run the full report search (not recommended).
      loadAllRowsFn()
        .then(() => {
          // set primary ID value from the saved row
          const savedRowId:number = _.isObject(savedRow) ? savedRow[idKey] : savedRow;
          let rowIndex:number = getRowIndex(savedRowId);

          // Add row selection (highlight the added row) if the row is found, as loadAllRowsFn should insert the new row after the function is resolved.
          if (rowIndex > -1) {
            return this.showAndSelectRow(gridId, rowIndex);
          } else if(savedRowId) { // do not load when savedRowId is not set, otherwise results in a loading without idKey/filters (a lot of results)
            // it depends on the selected filters the new row might be not visible, so regardless of filters we adding it
            this.loadRow(gridId, queryFilters, savedRowId, browseListConfig)
              .then((fetchSavedRow:IReportRow) => {
                this.reportGridService.addOrReplaceRows(gridId, [ <IReportRow>fetchSavedRow ], browseListConfig.dataBindings);
                rowIndex = this.reportGridService.getIndex(gridId, idKey, savedRowId);
                return this.showAndSelectRow(gridId, getRowIndex(savedRowId));
              });
          }
        });
    } else if (browseListConfig.reloadRows === ReloadRows.SINGLE) {
      // add a single row
      this.reportGridService.addOrReplaceRows(gridId, [ <IReportRow>savedRow ], browseListConfig.dataBindings);
      return this.showAndSelectRow(gridId, getRowIndex(savedRow[ idKey ]));
    } else {
      this.showReloadMessage();
    }
  }

  public editRow = (gridId:string, browseListConfig: IBrowseListConfig, visibleIndex: number, queryFilters: IObject, browselistContext?: IObject, editBtnElement?: HTMLButtonElement, title?: string, loadAllRowsFn?: () => Promise<IReportData | string>): Promise<string | IReportRow> => {
    const label: string = browseListConfig.label || title;
    const idx:number[] = this.verifySelection(gridId, label, visibleIndex);
    visibleIndex = idx && idx[ 0 ];
    if (util.notNullOrUndefinedOrEmpty(visibleIndex) && visibleIndex >= 0) {
      // highlight the row that is being editing
      this.showAndSelectRow(gridId, visibleIndex);

      if (editBtnElement) {
        // disabled button while rendering edit dialog screen
        this.reportGridService.setEditButtonDisabled(editBtnElement, true);
      }

      return this.edit(gridId, queryFilters, visibleIndex, browseListConfig, browselistContext, label, loadAllRowsFn)
        .then((savedRow: string | IReportRow) => {
          this.afterEdit(gridId, <IReportRow>savedRow, visibleIndex, browseListConfig);
          return savedRow;
        })
        .catch(util.noop)
        .finally(() => {
          if (editBtnElement) {
            this.reportGridService.setEditButtonDisabled(editBtnElement, false);
          }
        });
    }
  };

  private afterEdit(gridId: string, savedRow: IReportRow, visibleIndex: number, config: IBrowseListConfig): void {
    if (config.reloadRows === ReloadRows.NONE) {
      this.showReloadMessage();
    } else {
      // add timeout to let grid finish redraw rows when 'reloadRows' option is ALL
      setTimeout(() => {
        // handles the cases when reloadRows is set to SINGLE or ALL
        this.reportGridService.updateData(gridId, {
          [ this.reportGridService.getListIndex(gridId, visibleIndex) ]: {
            row: savedRow,
            actionStatus: ActionStatus.OK
          }
        });
      });
    }
  }

  private edit(gridId:string, queryFilters:any, rowIndex:number, config:IBrowseListConfig, browselistContext?: any, title?: string, loadAllRowsFn?: () => Promise<any>): Promise<string | IReportRow> {
    //make sure a single row is selected
    if (rowIndex < 0) {
      return Promise.reject('SELECTION');
    }

    //clear the action status
    this.reportGridService.clearActionStatus(gridId);

    const entityRow: IReportRow = this.reportGridService.getRowData(gridId, rowIndex);
    const entityId: number | string = this.getEntityId(entityRow, config);
    const componentConfig: IBrowseListComponentConfig = this.selectComponent(this.getBrowselistComponent(config, 'editComponent'), entityRow);

    const setRowStatus = (actionStatus: ActionStatus): void => {
      // update row status to show check button if success, loadRow will trigger when request to server is HTTP 200
      this.reportGridService.setRowStatus(gridId, rowIndex, actionStatus);
    };

    const loadRow = ():Promise<IReportRow|void> => {
      if (config.reloadRows === ReloadRows.SINGLE) {
        //if there's an idKey, and it is found in the row then we reload that single row
        return entityId && this.loadRow(gridId, queryFilters, entityId, config, entityRow);
      } else if (config.reloadRows === ReloadRows.ALL) {
        // when entityId is defined -> return the edited row from the complete list of report rows
        return loadAllRowsFn()
          .then((reportRows:IReportRow[]) => entityId ? reportRows[entityId] : undefined);
      } else {
        return Promise.resolve();
      }
    };

    if (componentConfig.use === GaVersion.GA1) {
      const editUrl:string = this.prepareGA1Url(componentConfig.url, queryFilters, config, entityRow);
      return this.showGA1AddEditComponentDialog('Edit', editUrl, config, loadRow, componentConfig, entityRow);
    } else if (componentConfig.use === GaVersion.GA2) {
      //load entity from server
      return this.loadEntityFromServer(entityRow, config)
        //show edit dialog
        .then((entity) => {

          const saveFunction:Function = (model:any):Promise<any> => {
            const msg = this.messageService.showStatusMessage(`Saving ${title} ...`);
            //send to server when ok is pressed
            //return result from server
            //reject when cancel is pressed or something is wrong
            return this.saveEntityOnServer(entityRow, model, config)
              .then(loadRow)
              .catch(() => {
                setRowStatus(ActionStatus.ERROR);
              })
              .finally(() => {
                this.messageService.closeMessage(msg);
              });
          };

          return this.showGA2AddEditComponentDialog(entity, config, componentConfig, entityRow, loadRow, saveFunction, queryFilters, title);
        });
    } else {
      return this.showGA3FormInDialog(entityRow, config, componentConfig, loadRow, queryFilters, setRowStatus, browselistContext, title);
    }
  }

  private selectComponent(multiComponentConfig:IBrowseListComponentConfig | IBrowseListComponentConfig[], entityRow:IReportRow): IBrowseListComponentConfig {
    let componentConfig: IBrowseListComponentConfig;
    if (_.isArray(multiComponentConfig)) {
      for (const cc of multiComponentConfig) {
        if (cc.condition) {
          let condition = cc.condition;
          // replace all placeholders e.g. {Dereqid}
          _.each(entityRow, (value, key) => {
            condition = condition.replace(`{${key}}`, util.extractValue(value));
          });

          try {
            // eslint-disable-next-line no-eval
            if (eval(condition)) {
              componentConfig = cc;
              break;
            }
          } catch (e) {
            console.warn(`Error evaluating browse list condition: ${cc.condition} => ${condition}`);
          }
        } else {
          componentConfig = cc;
          break;
        }
      }
      return componentConfig;
    } else {
      return <IBrowseListComponentConfig>multiComponentConfig;
    }
  }

  private showGA1AddEditComponentDialog(title:string, url:string, config:IBrowseListConfig, loadRow:Function, componentConfig:IBrowseListComponentConfig, entityRow:IReportRow): Promise<any> {
    const deferred: IGADeferred<any> = this.deferredService.defer();
    const dialogOptions: IDialogOptions = _.clone(componentConfig.dialogOptions);
    if (dialogOptions && dialogOptions.title && entityRow) {
      dialogOptions.title = this.formattingService.substituteText(dialogOptions.title, entityRow);
    }

    const preparedDialogOptions: IDialogOptions = util.copyAndExtend({
      translate: false,
      size: DialogSize.XL,
      buttons: [ { label: 'Close' } ]
    }, dialogOptions);

    const dialog: IDialog = this.messageService.showGA1Dialog(`${title} ${config.label}`, url, preparedDialogOptions);

    dialog.onHide.subscribe(() => {
      deferred.reject('Cancelled');
    });

    this.eventSubscription?.unsubscribe();
    this.eventSubscription = this.gaEventService.events.subscribe((evt: IGAEvent) => {
      if (evt.name === EVENT_CLOSE_DIALOG) {
        if (evt.data && evt.data !== 'Close') {
          const entityId: string | number = this.getEntityId(evt.data, config);
          const msg: Noty = this.messageService.showStatusMessage(
            this.translationService.instant('GA.LOADING', { toload: config.label })
          );
          // reload the row
          return loadRow(entityId, evt.data)
            .then(deferred.resolve)
            .catch(deferred.reject)
            .finally(() => {
              dialog.hide();
              this.messageService.closeMessage(msg);
              this.eventSubscription.unsubscribe();
            });
        } else {
          dialog.hide();
          this.eventSubscription.unsubscribe();
        }
      }
    });

    return deferred.promise;
  }

  /**
   ** @param entity {any} - entity contains of response when fetching data from config.restPath
   ** @param config {IBrowseListConfig} - contains of active browselist config
   ** @param componentConfig {IBrowseListComponentConfig} - contains of prepared/selected component config on action (add/edit)
   ** @param entityRow {IReportRow} - contain of all cells on selected row (reference value / column name), this will have full list if action = edit
   ** @param loadRowFunction {=Function} - a function to load row after add/edit
   ** @param saveFunction {=Function} - a function to save data
   ** @param queryFilters {=any} - object of query filters in report
   ** @param label {=string} the label to use for title and buttons
   ** @return Promise<string | IReportRow> response of save data
   */
  private showGA2AddEditComponentDialog(entity:any, config:IBrowseListConfig, componentConfig:IBrowseListComponentConfig, entityRow:IReportRow, loadRowFunction:Function, saveFunction:Function, queryFilters:any, label:string): Promise<string | IReportRow> {
    //this method will return the new/edited entity
    //or reject when the dialog was cancelled
    const deferred: IGADeferred<string | IReportRow> = this.deferredService.defer();
    const dialogOptions:IDialogOptions = _.clone(componentConfig.dialogOptions);

    if (dialogOptions && dialogOptions.title && entityRow) {
      dialogOptions.title = this.formattingService.substituteText(dialogOptions.title, entityRow);
    }

    const inputBindings: IGA2BrowselistInputBindings = {
      adding: !entity,
      saving: false,
      browselistContext: _.clone(entity),
      title: label,
      entity: label,
      queryFilters
    };

    // we cannot put inputBindings.model to undefined, or it will clear the form after saving
    // that's why we have to set it separately when we're using the dialog for editing an existing row
    if (util.notNullOrUndefined(entity)) {
      inputBindings.model = entity;
    }

    // !!! ensure that these methods (onDialogSaving, onSave, onDialogSaved and onCancel) are not used in your dialog component, as the output bindings won't work as expected (these output bindings stop receiving events)
    const outputBindings: IGA2BrowselistOutputBindings  = {
      onDialogSaving: dialogOnSaving.bind(this),
      onSave: dialogSave.bind(this),
      onDialogSaved: dialogOnSaved.bind(this),
      onCancel: dialogCancel
    };

    let msg: Noty;
    const componentAttributes: IBrowseListComponentAttributes = _.clone(<IBrowseListComponentAttributes>componentConfig.component);

    // add component input bindings defined in config and applied it to 'inputBindings' variable
    // noinspection DuplicatedCode
    _.each(componentAttributes.inputBindings, (val: any, bindingKey: string) => {
      let bindingValue: any;

      if (_.isFunction(val)) {
        bindingValue = val.call(this, inputBindings);
      } else {
        bindingValue = val;
      }

      inputBindings[bindingKey] = bindingValue;
    });

    // add component output bindings defined in config and applied it to 'outputBindings' variable
    _.each(componentAttributes.outputBindings, (val: Function, bindingKey: string) => {
      outputBindings[bindingKey] = val.call(this, outputBindings);
    });

    // add data bindings referenced from report row value to default attributes binding
    if (entityRow) {
      _.each(config.dataBindings, (column:string, dataBinding:string) => {
        inputBindings[dataBinding] = util.extractValue(entityRow[column] || column);
      });
    }

    // browselist dialog component should use 'app'
    this.stateService.screen = `${TranslationCategory.APP}.${this.stateService.currentView}`;

    const dialog: IDialog = this.messageService.showDialog({
      component: componentAttributes.name,
      inputs: inputBindings,
      outputs: outputBindings,
      wrapperComponent: false,
      closeCallback: dialogCancel,
      closeButton: componentAttributes.closeButton,
      size: DialogSize.MD, // default size
      ...dialogOptions
    });

    dialog.onHide.subscribe(() => {
      this.stateService.screen = undefined;

      if (dialog) {
        dialogCancel();
      }
    });

    function dialogCancel(): void {
      deferred.reject('Cancelled');
      dialog.hide();
    }

    function dialogOnSaving(saving: boolean): void {
      if (saving) {
        msg = this.messageService.showStatusMessage(`Saving ${label} ...`);
      } else {
        this.messageService.closeMessage(msg);
      }
    }

    // this method will be invoked when browselist using default ga2 dialog form component
    function dialogSave(model: any): void {
      inputBindings.saving = true;
      saveFunction(_.clone(model))
        .then((savedModel: IReportRow) => {
          deferred.resolve(savedModel);
        })
        .catch((error:string) => {
          this.messageService.showErrorMessage(error);
        })
        .finally(() => {
          dialog.hide();
          inputBindings.saving = false;
        });
    }

    function dialogOnSaved(responseData: any): void {
      const entityId: number | string = this.getEntityId(entityRow, config);
      const close = (): void => {
        // destroy/close dialog and message
        this.messageService.closeMessage(msg);
        dialog.hide();
      };

      // when entityId is set, it indicates that the saved row is being edited. Even if the PUT request does not return the edited row id (responseData is empty), the edited row id is known, so call the loadRowFunction to update the row data
      if (responseData || entityId) {

        if (loadRowFunction) {
          // loadRow expects parameter when action = add
          // when in edit mode, loadRow is not expecting any parameter since dataBindings.idKey already set
          loadRowFunction(responseData?.id, entityRow)
            .then(rowData => {
              deferred.resolve(rowData);
              close();
            })
            .catch(() => {
              // when we don't receive response from loadRowFunction (empty results) then we still should close dialog and message
              // prevent stuck on saving screen
              deferred.reject();
              close();
            });
        } else {
          // when the browselist is open in a dialog from ga1 is not defined a loadRowFunction -> resolve the promise with the response data
          deferred.resolve(responseData);
          close();
        }
      } else {
        deferred.resolve();
        close();
      }
    }

    return deferred.promise;
  }

  /**
   * @param entityRow {IReportRow} - report row entity
   * @param config {IBrowseListConfig} - Browselist config
   * @param componentConfig {IBrowseListComponentConfig} - contains of prepared/selected component config on action (add/edit)
   * @param loadRowFunction {Function} - a function to load row after add/edit
   * @param queryFilters {IObject}
   * @param setRowStatus {Function}
   * @param ga3FormContextValues {IObject}
   * @param label {string} - dialog label
   * @return Promise<IReportRow> - reloaded data row
   */
  private showGA3FormInDialog(
    entityRow:IReportRow,
    config:IBrowseListConfig,
    componentConfig:IBrowseListComponentConfig,
    loadRowFunction:(id?:string | number, entityRow?:IObject) => Promise<IReportRow|void>,
    queryFilters:IObject,
    setRowStatus:(actionStatus: ActionStatus) => void,
    ga3FormContextValues:IObject,
    label:string
  ):Promise<string | IReportRow> {
    const deferred: IGADeferred<string | IReportRow> = this.deferredService.defer();
    const dialogOptions: IDialogOptions = _.clone(componentConfig.dialogOptions);
    const dataKeys:string[] = _.keys(_.omit(config.dataBindings, REPORT_CELL_PROPERTIES));

    let msg: Noty;
    let keys: string | string[];

    if (dataKeys.length === 1) {
      keys = entityRow? util.extractValue(<string>entityRow[config.dataBindings.idKey]) : undefined;
    } else {
      keys = <string[]>this.reportFiltersUtil.filtersToParam(this.reportGridService.transformDataBindingsToValue(config?.dataBindings, entityRow, true));
    }

    // prepare componentConfig context, transform reference value based on report filter column name
    let context: Dictionary<IGAFormTypeValue>;

    if (componentConfig.context) {
      context = this.transformGA3ContextValue(componentConfig.context, queryFilters, entityRow, ga3FormContextValues);
    }

    const inputBindings: IObject = {
      key: keys,
      formId: componentConfig.formId,
      reportId: config.reportId,
      isInDialog: true,
      context,
      compositeKeyForm: componentConfig.compositeKeyForm,
      title: label,
      showPostActionMessage: config.reloadRows !== ReloadRows.NONE
    };

    const outputBindings: IObject = {
      onSaving: onSaving.bind(this),
      onSaved: onSaved.bind(this),
      onDialogCancel: dialogCancel.bind(this),
      onError: onError.bind(this)
    };

    const dialog:IDialog = this.messageService.showDialog({
      component: FormsComponent,
      inputs: inputBindings,
      outputs: outputBindings,
      wrapperComponent: false,
      closeCallback: dialogCancel,
      ...dialogOptions
    });

    dialog.onHide.subscribe(() => {
      if (dialog) {
        dialogCancel();
      }
    });

    function dialogCancel(): void {
      deferred.reject('Cancelled');
      dialog.hide();
    }

    function onSaving(saving: boolean): void {
      if (saving) {
        msg = this.messageService.showStatusMessage(this.translationService.instant('GA.GENERAL.SAVING', { label: label }));
      } else {
        this.messageService.closeMessage(msg);
      }
    }

    function onSaved(keys: IObject): void {
      if (keys && !_.isEmpty(keys)) {
        const isSingleKey:boolean = _.keys(keys).length === 1;
        const idKey:string | number = isSingleKey? keys[_.keys(keys)[0]] : undefined;

        // loadRow expects parameter when action = add
        // in edit mode, loadRow is not expecting any parameter since dataBindings already set
        // in ga3, keys parameter will be treated as entity row and idKey will be available only when single key defined in YAML form.
        // If there are multiple keys idKey will be ignored, this is to support multiple keys defined in ga3 yaml form (composite keys / multiple keys)
        loadRowFunction(idKey, keys)
          .then((rowData:IReportRow) => {
            deferred.resolve(rowData);
          })
          .catch(() => {
            // when we don't receive response from loadRowFunction (empty results) then we still should close dialog and message
            // prevent stuck on saving screen
            deferred.reject();
          })
          .finally(() => {
            // destroy dialog and message
            this.messageService.closeMessage(msg);
            dialog.hide();
          });
      } else {
        dialogCancel();
      }
    }

    function onError(): void {
      if (setRowStatus) {
        setRowStatus(ActionStatus.ERROR);
      }
    }

    return deferred.promise;
  }

  private loadEntityFromServer(entityRow:IReportRow, config:IBrowseListConfig):Promise<any> {
    if (!config.noFetch) {
      return this.httpDataService.doGET(
        this.formattingService.substituteText(config.restPath, entityRow),
        undefined,
        undefined,
        `Error loading ${config.label}`
      );
    } else {
      // if rest path is not defined, return entityId
      return Promise.resolve(this.getEntityId(entityRow, config));
    }
  }

  public loadRow(gridId:string, currentFilterModel:any, idValue:string | number, config:IBrowseListConfig, entityRow?:IReportRow):Promise<IReportRow> {
    const reportMetadata = this.reportGridService.getMetaData(gridId);
    let filterModel:any = {};

    _.forEach(reportMetadata.columns, (c) => {
      if (c.formatting && c.formatting.alwaysIncludeInSearch) {
        filterModel[ c.name ] = currentFilterModel[ c.name ];
      }
    });
    // if entity row is provided (for ga3 add record, and edit row ga1-ga3)
    if (entityRow) {
      // combined with entityRow to fetch single row based on selected row / defined properties in entityRow
      filterModel = util.copyAndExtend(filterModel, this.reportGridService.transformDataBindingsToValue(config?.dataBindings, entityRow));
    }

    // if id value is provided (for single key, ga1 & ga2)
    if (idValue) {
      filterModel[ config.dataBindings.idKey ] = idValue;
    }

    const queryFilters = this.reportFiltersUtil.getQueryFiltersFromModel(filterModel, reportMetadata, {
      applyDefaultValues: false,
      applyNullValues: true
    });

    // !! at this point the report should only return a single row: only the one that is edited/added
    return this.reportDataService.loadData(`${config.reportId}`, config.reportId, queryFilters)
      .then((reportData:IReportResponse) => {
        const rows:IReportRow[] = reportData.data.data;
        if (rows.length === 1) {
          return rows[ 0 ];
        } else {
          return Promise.reject(`Error: The results cannot be displayed (${rows.length} rows received instead of 1). Check report filters`);
        }
      });
  }

  private addEntityToServer(config: IBrowseListConfig, entityRow:IReportRow):Promise<any> {
    const prepareEntityRow:{ [ key:string ]:string } = this.transformModelIntoDataBindingsValue(config, entityRow);
    return this.httpDataService.doPOST(
      this.formattingService.substituteText(config.addRestPath, prepareEntityRow),
      undefined,
      entityRow,
      undefined,
      `Error adding ${config.label}`
    );
  }

  private saveEntityOnServer(entityRow:IReportRow, model:any, config:IBrowseListConfig):Promise<any> {
    return this.httpDataService.doPUT(
      this.formattingService.substituteText(config.restPath, entityRow),
      undefined,
      model,
      undefined,
      `Error saving ${config.label}`
    );
  }


  private deleteEntityFromServer(config:IBrowseListConfig, entityRow:IReportRow, reportId?:string, browselistContext?: IObject, label?: string):Promise<any> {
    // try getting editComponent config first else then get addComponent config
    const componentConfig:IBrowseListComponentConfig = <IBrowseListComponentConfig>(config? this.getBrowselistComponent(config, 'editComponent') || this.getBrowselistComponent(config, 'addComponent') : undefined);

    if (!componentConfig) {
      console.warn(`This browselist doesn't have component config (edit/add)`);
      return;
    }

    if (componentConfig.use !== GaVersion.GA3) {
      return this.httpDataService.doDELETE(
        this.formattingService.substituteText(config.restPath, entityRow),
        undefined,
        undefined,
        `Error deleting ${label}`
      );
    } else {
      const action:IGAFormAction[] = [
        {
          id: EVENT_DELETE_ACTION,
          type: TYPE_DELETE
        }
      ];
      const deferred: IGADeferred<void> = this.deferredService.defer();
      const showErrorMessage = (message: string): void => {
        if (message) {
          this.messageService.showErrorMessage(message);
        }
        deferred.reject();
      };

      let keys:string | string[];

      if (_.keys(config.dataBindings).length === 1) {
        keys = entityRow ? util.extractValue(entityRow?.[ config.dataBindings.idKey ]) : undefined;
      } else {
        keys = <string[]>this.reportFiltersUtil.filtersToParam(this.reportGridService.transformDataBindingsToValue(config?.dataBindings, entityRow, true));
      }

      if (componentConfig.context) {
        componentConfig.context = this.transformGA3ContextValue(componentConfig.context, undefined, entityRow, browselistContext);
      }

      // defined call context
      const callContext: IGA3CallContext = {
        type: CallContextType.REPORT,
        reportId: reportId && Number(reportId),
        selectedKeys: undefined
      };

      this.getGA3FormMetadata(keys, componentConfig, callContext)
        .then((metadata:IGAFormMetadata) => {
          const requestBody:IGAFormActionEventRequest = this.formsService.createActionRequest(undefined, metadata.sections, action, { key: util.extractValue(entityRow?.[ config.dataBindings.idKey ]) }, callContext);
          this.formsDataService.postActionEvent(componentConfig.formId, requestBody)
            .then(() => {
              this.messageService.showFeedbackMessage(this.translationService.instant('GA.GENERAL.STATUS_MESSAGE_DELETED', { label }));
              deferred.resolve();
            })
            .catch(showErrorMessage);
        })
        .catch(showErrorMessage);

      return deferred.promise;
    }
  }

  private getGA3FormMetadata(key:string | string[], componentConfig: IBrowseListComponentConfig, callContext?: IGA3CallContext):Promise<IGAFormMetadata | string> {
    return this.formsDataService.postActionEvent(componentConfig.formId, this.formsService.initEventRequest(key, componentConfig.context, componentConfig.compositeKeyForm, callContext));
  }

  private transformGA3ContextValue(context: Dictionary<IGABrowselistFormTypeValue>, queryFilters?: any, entityRow?: any, contextValues?: IObject): Dictionary<IGABrowselistFormTypeValue> {
    // copy the object to not reference to original context config
    let formContext: Dictionary<IGABrowselistFormTypeValue> = util.deepClone(context);

    _.each(formContext, (ctx: IGABrowselistFormTypeValue, key: string) => {
      formContext = _.extend(formContext || {}, { [key]: util.deepClone(ctx) });

      if (_.isArray(ctx.valueOrigin)) {
        for (let i = 0; i < ctx.valueOrigin.length; i++) {
          const value: string | number = this.getValueFromValueOrigin(ctx.valueOrigin[i], ctx.value[ctx.valueOrigin[i]], queryFilters, entityRow, contextValues);
          formContext[key].value = value;

          // if value is not empty then break the loop
          if (util.notNullOrUndefinedOrEmpty(value)) {
            break;
          }
        }
      } else {
        formContext[key].value = this.getValueFromValueOrigin(ctx.valueOrigin, ctx.value, queryFilters, entityRow, contextValues);
      }

      formContext[key].visibility = (ctx.notEditableWhenFilled && formContext[key].value) ? GA3FormVisibility.READONLY : GA3FormVisibility.EDITABLE;
    });

    return formContext;
  }

  private getValueFromValueOrigin(valueOrigin: ValueOrigin, valueRef: string, queryFilters?: IObject, entityRow?: IObject, contextValues?: IObject): string | number {
    switch(valueOrigin) {
      case ValueOrigin.REPORT_FILTERS: {
        return this.transformEmptyValue(Number(queryFilters?.[ valueRef ]) || queryFilters?.[ valueRef ]);
      }
      case ValueOrigin.REPORT_ROW_VALUE: {
        return this.transformEmptyValue(Number(entityRow?.[valueRef]) || entityRow?.[valueRef]);
      }
      case ValueOrigin.COMPONENT_CONTEXT: {
        return this.transformEmptyValue(Number(contextValues?.[valueRef]) || contextValues?.[valueRef]);
      }
      case ValueOrigin.FIXED: {
        return valueRef;
      }
    }
  }

  private transformEmptyValue(value: any): string | number {
    // define here for list of forbidden values in report filter
    const forbiddenValues: string[] = [
      'null',
      'undefined'
    ];

    if (util.isNullOrUndefinedOrEmpty(value) || !!~forbiddenValues.indexOf(value)) {
      return null;
    } else {
      return util.extractValue(value);
    }
  }

  private checkPreconditionFunction(config:IBrowseListConfig, queryFilters:IObject) {
    if (config.precondition) {
      const precondition: BrowselistsPrecondition = this.injector.get(config.precondition);
      const errorMessage:string | void = precondition.execute(queryFilters);

      if (errorMessage) {
        this.messageService.showErrorDialog(this.translationService.instant('GA.VALIDATION.VALIDATION_FAILED'), errorMessage);
      }

      return errorMessage;
    }
  }

  // this opens the browselist edit in a dialog from others screens than reports/browselist (e.g. from order ga1 screen)
  public openBrowselistEditDialog(data:{ listCode:string; params:any }):void {
    const config:IBrowseListConfig = <IBrowseListConfig>this.browseListConfigService.getConfig(data.listCode);
    const componentConfig:IBrowseListComponentConfig = <IBrowseListComponentConfig>this.getBrowselistComponent(config, 'editComponent');
    const showBrowselistEditDialog = () => {
      if (componentConfig.use === GaVersion.GA2) {
        //load entity from server
        return this.loadEntityFromServer(data.params, config)
          //show edit dialog
          .then((entity) => {
            return this.showGA2AddEditComponentDialog(entity, config, componentConfig, data.params, undefined, undefined, undefined, config.label);
          });
      }
    };

    showBrowselistEditDialog()
      .then((data:{}) => {
        this.ga1Service.sendMessage(MESSAGE_BROWSELIST_EDIT_DIALOG_CLOSED, data);
      });
  }

  private showReloadMessage(): void {
    this.messageService.showFeedbackMessage('<i class="fa fa-warning"></i> Please reload your report to see your changes', MessageType.WARNING);
  }
}